import {
  AppModuleCode,
  PermissionCode,
  type TicketListItem,
  type TicketListQuery,
} from '@gb-ticket/shared-types';
import { AlertTriangleIcon, Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import { OPEN_TICKET_STATUSES } from '@/products/helpdesk/features/tickets/home-ticket-queue';
import { PriorityBadge, StatusBadge } from '@/products/helpdesk/features/tickets/ticket-badges';
import { ticketListPath } from '@/products/helpdesk/features/tickets/ticket-list-path';
import { useTickets } from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

const MAJOR_PREVIEW = 3;

const majorQuery: TicketListQuery = {
  page: 1,
  pageSize: MAJOR_PREVIEW,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  isMajor: true,
  status: [...OPEN_TICKET_STATUSES],
};

export function HomeMajorIncidentsBanner() {
  const me = useMe();
  const incidentsOn = useModule(AppModuleCode.INCIDENTS);
  const canSee = incidentsOn && hasPermission(me.data, PermissionCode.TICKETS_READ_ALL);
  const tickets = useTickets(majorQuery, { enabled: canSee });

  if (!canSee) {
    return null;
  }

  const total = tickets.data?.total ?? 0;
  const items = tickets.data?.items ?? [];
  const href = ticketListPath({
    isMajor: true,
    status: [...OPEN_TICKET_STATUSES],
  });
  const hasActive = total > 0;

  return (
    <div
      role="region"
      aria-label="Aktive Major Incidents"
      className={cn(
        'min-w-0 overflow-hidden rounded-xl border px-4 py-3 sm:px-5',
        hasActive
          ? 'border-amber-300/80 bg-amber-50 text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-50'
          : 'border-dashed bg-muted/40',
      )}
    >
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <AlertTriangleIcon
              className={cn(
                'size-4 shrink-0',
                hasActive ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground',
              )}
              aria-hidden
            />
            <h2 className="text-sm font-semibold tracking-tight sm:text-base">
              Aktive Major Incidents
              {!tickets.isPending && !tickets.isError ? (
                <span className="ml-2 font-normal text-muted-foreground tabular-nums">
                  ({total})
                </span>
              ) : null}
            </h2>
          </div>
          {tickets.isPending ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Werden geladen …
            </p>
          ) : null}
          {tickets.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {tickets.error instanceof ApiRequestError
                ? tickets.error.message
                : 'Major Incidents konnten nicht geladen werden.'}
            </p>
          ) : null}
          {!tickets.isPending && !tickets.isError && total === 0 ? (
            <p className="text-sm text-muted-foreground">Keine aktiven Major Incidents.</p>
          ) : null}
          {!tickets.isPending && !tickets.isError && items.length > 0 ? (
            <ul className="mt-1 divide-y divide-border rounded-md border bg-background/70">
              {items.map((item) => (
                <MajorRow key={item.id} item={item} />
              ))}
            </ul>
          ) : null}
        </div>
        <Button asChild variant="outline" size="sm" className="shrink-0 self-start bg-background">
          <Link to={href}>Alle anzeigen</Link>
        </Button>
      </div>
    </div>
  );
}

function MajorRow({ item }: { item: TicketListItem }) {
  return (
    <li>
      <Link
        to={`/helpdesk/tickets/${item.number}`}
        className="flex min-w-0 flex-col gap-1 px-3 py-2 hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between"
      >
        <p className="min-w-0 truncate text-sm font-medium">
          #{item.number} · {item.subject}
        </p>
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <StatusBadge status={item.status} />
          <PriorityBadge priority={item.priority} />
        </span>
      </Link>
    </li>
  );
}
